import { useState, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiGet } from "../lib/api";
import { COLORS } from "../lib/colors";

function formatTime(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function CompanyLogo({ company, size = 38 }) {
  const letters = (company || "?").substring(0, 2).toUpperCase();
  const colors = [
    { bg: "#EBF0FD", fg: "#2B5CE6" },
    { bg: "#E8F5EF", fg: "#1A8754" },
    { bg: "#FFF4E5", fg: "#C4720A" },
    { bg: "#F0EBFA", fg: "#6B46C1" },
  ];
  const idx = (company?.charCodeAt(0) || 0) % colors.length;
  const { bg, fg } = colors[idx];
  return (
    <div style={{
      width: size, height: size, borderRadius: 10,
      backgroundColor: bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 13, fontWeight: 700, color: fg, flexShrink: 0,
    }}>
      {letters}
    </div>
  );
}

function ReferCard() {
  const [copied, setCopied] = useState(false);

  const shareUrl = window.location.origin;
  const shareText = `I'm on Relay — recruiters at my uni can find my CV there and I've already had companies open it. Worth checking if your school is on it too: ${shareUrl}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(shareText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  return (
    <div style={{
      margin: "0 20px 16px",
      padding: "16px 18px",
      backgroundColor: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>
        Know someone who should be here?
      </div>
      <div style={{ fontSize: 12, color: COLORS.textSecondary, lineHeight: 1.5, marginBottom: 14 }}>
        Recruiters are finding students through Relay. Tell a classmate — the more students join, the better it gets for everyone.
      </div>
      <button
        onClick={handleCopy}
        style={{
          width: "100%",
          padding: "9px 0",
          borderRadius: 8,
          border: `1px solid ${copied ? COLORS.green : COLORS.accent}`,
          backgroundColor: copied ? COLORS.greenLight : COLORS.accentLight,
          color: copied ? COLORS.green : COLORS.accent,
          fontSize: 13,
          fontWeight: 600,
          fontFamily: "'DM Sans', sans-serif",
          cursor: "pointer",
          transition: "all 0.2s",
        }}
      >
        {copied ? "✓ Copied!" : "Copy message to share"}
      </button>
    </div>
  );
}

export default function DashboardTab({ onNavigateRequests, onPendingCount }) {
  const { candidate, session } = useAuth();
  const [requests, setRequests] = useState([]);
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);

  const firstName = candidate?.name?.split(" ")[0] || "there";

  useEffect(() => {
    if (!candidate?.id || !session?.access_token) return;
    loadData();
  }, [candidate?.id, session?.access_token]);

  const loadData = async () => {
    try {
      const [reqData, viewData] = await Promise.all([
        apiGet(`/cv-requests/${candidate.id}`, session.access_token),
        apiGet(`/student/activity`, session.access_token),
      ]);
      const reqs = reqData.requests || [];
      setRequests(reqs);
      setViews(viewData.views || []);
      const pending = reqs.filter((r) => r.status === "pending").length;
      onPendingCount?.(pending);
    } catch (err) {
      console.error("Failed to load dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  const pending = requests.filter((r) => r.status === "pending").length;
  const approved = requests.filter((r) => r.status === "approved").length;
  const totalViews = views.length;

  // Merge views and requests into one activity feed, sorted newest first
  const viewEvents = views.map((v) => ({
    type: "view",
    id: `view-${v.viewed_at}-${v.recruiter_id}`,
    company: v.company || v.recruiter_email?.split("@")[1]?.split(".")[0] || "A recruiter",
    recruiterName: v.recruiter_name,
    timestamp: v.viewed_at,
    status: "view",
  }));

  const requestEvents = requests.map((r) => ({
    type: "request",
    id: r.id,
    company: r.recruiter?.company || "Unknown company",
    recruiterName: r.recruiter?.name || "",
    recruiterStatus: r.recruiter?.status,
    searchQuery: r.search_query,
    matchScore: r.match_score,
    timestamp: r.requested_at,
    status: r.status,
  }));

  const feed = [...viewEvents, ...requestEvents]
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, 12);

  const totalActivity = feed.length;

  return (
    <div>
      {/* Greeting */}
      <div style={{ padding: "20px 20px 0" }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: COLORS.text, margin: 0 }}>
          Hey, {firstName} 👋
        </h1>
        <p style={{ fontSize: 14, color: COLORS.textSecondary, marginTop: 4, marginBottom: 0 }}>
          {loading
            ? "Loading your activity..."
            : totalActivity === 0
            ? "Your profile is live — recruiters can find you now"
            : `${totalActivity} thing${totalActivity !== 1 ? "s" : ""} have happened on your profile`}
        </p>
      </div>

      {/* Stats row */}
      <div style={{ display: "flex", gap: 10, padding: "16px 20px" }}>
        <div style={{
          flex: 1,
          backgroundColor: COLORS.accentLight,
          borderRadius: 12,
          padding: "14px 12px",
          textAlign: "center",
        }}>
          <div style={{ fontSize: 26, fontWeight: 700, color: COLORS.accent }}>
            {loading ? "–" : totalViews}
          </div>
          <div style={{ fontSize: 11, color: COLORS.accent, opacity: 0.8, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 2 }}>
            CV Opens
          </div>
        </div>
        <div style={{
          flex: 1,
          backgroundColor: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 12,
          padding: "14px 12px",
          textAlign: "center",
        }}>
          <div style={{ fontSize: 26, fontWeight: 700, color: COLORS.text }}>
            {loading ? "–" : requests.length}
          </div>
          <div style={{ fontSize: 11, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 2 }}>
            Requests
          </div>
        </div>
        <div style={{
          flex: 1,
          backgroundColor: approved > 0 ? COLORS.greenLight : COLORS.surface,
          border: `1px solid ${approved > 0 ? "transparent" : COLORS.border}`,
          borderRadius: 12,
          padding: "14px 12px",
          textAlign: "center",
        }}>
          <div style={{ fontSize: 26, fontWeight: 700, color: approved > 0 ? COLORS.green : COLORS.text }}>
            {loading ? "–" : approved}
          </div>
          <div style={{ fontSize: 11, color: approved > 0 ? COLORS.green : COLORS.textMuted, opacity: approved > 0 ? 0.8 : 1, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 2 }}>
            Approved
          </div>
        </div>
      </div>

      {/* Pending CTA */}
      {pending > 0 && (
        <div
          onClick={onNavigateRequests}
          style={{
            margin: "0 20px 16px",
            padding: "14px 18px",
            background: `linear-gradient(135deg, ${COLORS.accent}, #4070f4)`,
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>
              {pending} recruiter{pending > 1 ? "s want" : " wants"} your CV
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.8)", marginTop: 2 }}>
              Tap to review and respond →
            </div>
          </div>
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            backgroundColor: "rgba(255,255,255,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 16, color: "#fff",
          }}>
            {pending}
          </div>
        </div>
      )}

      {/* Refer a classmate — appears after first CV view */}
      {!loading && totalViews > 0 && (
        <ReferCard school={candidate?.school_name} />
      )}

      {/* Activity Feed */}
      <div style={{ padding: "0 20px 24px" }}>
        <div style={{
          fontSize: 11, fontWeight: 600, textTransform: "uppercase",
          letterSpacing: "0.1em", color: COLORS.textMuted, marginBottom: 12,
        }}>
          Recent Activity
        </div>

        {loading ? (
          <>
            {[1, 2, 3].map((i) => (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "12px 0",
                borderBottom: `1px solid ${COLORS.borderLight}`,
              }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, backgroundColor: COLORS.surfaceAlt }} />
                <div style={{ flex: 1 }}>
                  <div style={{ height: 13, width: "60%", borderRadius: 4, backgroundColor: COLORS.surfaceAlt, marginBottom: 6 }} />
                  <div style={{ height: 11, width: "40%", borderRadius: 4, backgroundColor: COLORS.surfaceAlt }} />
                </div>
              </div>
            ))}
          </>
        ) : feed.length === 0 ? (
          <div style={{ textAlign: "center", padding: "32px 20px" }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
            <p style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>
              You're in the pool
            </p>
            <p style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.5 }}>
              Recruiters at your university can find your profile. When they do, you'll see it here.
            </p>
          </div>
        ) : (
          feed.map((item) => {
            if (item.type === "view") {
              return (
                <div key={item.id} style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "12px 0",
                  borderBottom: `1px solid ${COLORS.borderLight}`,
                }}>
                  <CompanyLogo company={item.company} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
                      {item.company || "A recruiter"} opened your CV
                    </div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 1 }}>
                      {item.recruiterName && item.recruiterName !== "A recruiter" ? item.recruiterName : "Recruiter"}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, flexShrink: 0 }}>
                    {formatTime(item.timestamp)}
                  </div>
                </div>
              );
            }

            // Request event
            const icon =
              item.status === "approved" ? "✓" :
              item.status === "pending" ? "◈" : "✕";
            const iconBg =
              item.status === "approved" ? COLORS.greenLight :
              item.status === "pending" ? COLORS.accentLight : COLORS.redLight;
            const iconColor =
              item.status === "approved" ? COLORS.green :
              item.status === "pending" ? COLORS.accent : COLORS.red;
            const label =
              item.status === "approved" ? "CV approved for" :
              item.status === "pending" ? "CV requested by" :
              item.status === "declined" ? "Declined from" : "Revoked from";

            return (
              <div key={item.id} style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "12px 0",
                borderBottom: `1px solid ${COLORS.borderLight}`,
              }}>
                <div style={{
                  width: 38, height: 38, borderRadius: "50%",
                  backgroundColor: iconBg, color: iconColor,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 14, flexShrink: 0,
                }}>
                  {icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: COLORS.text }}>
                    {label} {item.company}
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 1, display: "flex", alignItems: "center", gap: 6 }}>
                    <span>
                      {item.recruiterName}
                      {item.searchQuery ? ` · "${item.searchQuery}"` : ""}
                    </span>
                    {item.recruiterStatus === "revoked" && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, color: COLORS.textMuted,
                        backgroundColor: COLORS.surfaceAlt, padding: "1px 6px",
                        borderRadius: 4, lineHeight: "16px",
                      }}>
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 11, color: COLORS.textMuted, flexShrink: 0 }}>
                  {formatTime(item.timestamp)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
